import {
  Card,
  GeneratedCardContent,
  WordSense,
  cardFromGeneratedContent,
  normalizeTranslationLanguage,
  refreshCardContent,
  upNextFields,
  validateGeneratedContent,
  validateWordSenses,
} from '@lingoping/core';
import { FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { platform } from '../platform.js';
import { StorageManager } from '../storage/index.js';
import { AccessLockedError, throwIfAccessLocked } from './access.js';
import { buildPendingCard, rememberPendingCard } from './pendingCardStore.js';

/** Where a newly chosen word comes from: the learner (popup, right-click, "Add word") or a deck. */
export type UpNextSource = Parameters<typeof upNextFields>[0];

/** Puts a new card on the learner's "Up next to learn" list (own words first, then deck words). */
export function withUpNext(card: Card, source: UpNextSource, now?: Date): Card {
  return { ...card, ...upNextFields(source, now) };
}

/** Why a card could not be created. */
export type CardErrorCode =
  | 'not_a_german_word'
  | 'invalid_word'
  | 'daily_limit_reached'
  | 'sign_in_required'
  | 'offline'
  | 'unconfigured'
  | 'duplicate'
  | 'choose_sense'
  | 'other';

/**
 * Failures that say nothing about the word itself: the card can be made later, so a chosen word
 * becomes a "pending" card (see pendingCards.ts) instead of an error.
 */
export function isRetryableCode(code: CardErrorCode | string | undefined): boolean {
  return code === 'offline' || code === 'daily_limit_reached' || code === 'other';
}

/** A card could not be created; `message` is written for the user. */
export class CardCreationError extends Error {
  constructor(
    message: string,
    public readonly code: CardErrorCode = 'other'
  ) {
    super(message);
  }
}

/** The word is already in the user's deck; `card` is the existing card (shown instead). */
export class DuplicateCardError extends CardCreationError {
  constructor(public readonly card: Card) {
    super(`"${card.front_text}" is already in your deck.`, 'duplicate');
  }
}

/**
 * The word has several separate meanings and nothing said which one is meant: the learner chooses
 * (components/SenseChooser.tsx), and the card is asked for again with that meaning.
 */
export class SenseChoiceNeeded extends CardCreationError {
  constructor(
    /** The dictionary form the meanings belong to. */
    public readonly lemma: string,
    /** The meanings, most common first. */
    public readonly senses: WordSense[],
    /** The meanings the learner already has a card for (`sense_key`s). */
    public readonly have: string[],
    /** The meaning the word's sentence points at, to be ticked first. */
    public readonly suggested?: string
  ) {
    super(`"${lemma}" has several meanings.`, 'choose_sense');
  }
}

/** "der See" -> "See": a typed article is not part of the word a card is filed under. */
function withoutArticle(word: string): string {
  const match = /^(?:der|die|das)\s+(\S+)$/i.exec(word.trim());
  return match ? match[1] : word.trim();
}

/** The learner's cards for a word, every meaning; `ignoreId` skips a card waiting to be filled. */
async function cardsFor(word: string, language: string, ignoreId?: string): Promise<Card[]> {
  const key = word.trim().toLowerCase();
  return (await StorageManager.getCards()).filter(
    (c) =>
      c.id !== ignoreId &&
      (c.target_language || 'de') === language &&
      (c.lemma ?? c.front_text).toLowerCase() === key
  );
}

/**
 * The learner's card for a word; with `senseKey`, for that meaning of it. `ignoreId` skips the card
 * that is itself waiting to be filled.
 */
async function existingCardFor(
  word: string,
  language: string,
  ignoreId?: string,
  senseKey?: string
): Promise<Card | undefined> {
  const cards = await cardsFor(word, language, ignoreId);
  return senseKey === undefined ? cards[0] : cards.find((c) => (c.sense_key ?? '') === senseKey);
}

/**
 * Turns generated content into a card, unless its dictionary form is already one of the user's
 * cards (duplicate). The AI often returns the dictionary form of an inflected input ("Schiffe" ->
 * "Schiff"), so this is checked again here.
 */
async function cardForContent(
  content: GeneratedCardContent,
  userId: string,
  ignoreId?: string
): Promise<Card> {
  // One card per meaning: "Bank" the bench does not stand in the way of "Bank" the bank.
  const existing = await existingCardFor(
    content.lemma,
    content.language,
    ignoreId,
    content.sense_key
  );
  if (existing) throw new DuplicateCardError(existing);
  return cardFromGeneratedContent(content, userId);
}

const ERROR_MESSAGES: Record<string, string> = {
  sign_in_required: 'Please sign in to add new words.',
  daily_limit_reached: "You've reached today's limit of 100 new words. Try again tomorrow.",
  not_a_german_word: "doesn't look like a German word. Check the spelling and try again.",
  invalid_word: 'Only single words or short phrases can be added.',
};

const KNOWN_CODES: CardErrorCode[] = [
  'not_a_german_word',
  'invalid_word',
  'daily_limit_reached',
  'sign_in_required',
];

/** The server's error code when it is one we know; anything else is a plain server error. */
function knownCode(code: string | undefined): CardErrorCode {
  return KNOWN_CODES.find((c) => c === code) ?? 'other';
}

async function httpErrorCode(error: FunctionsHttpError): Promise<string | undefined> {
  try {
    return (await (error.context as Response).clone().json())?.error;
  } catch {
    return undefined;
  }
}

/**
 * Card text for a word, from the `generate-card` Edge Function: the shared copy when another user
 * already added the word, otherwise generated once by AI on the server and shared from then on.
 * `hint` is an optional translation from an imported file, passed through only as a hint for the
 * AI to weigh - it still writes the card itself.
 */
async function fetchGeneratedContent(
  word: string,
  language: string,
  context?: string,
  hint?: string,
  /** Set when this device may hold several cards of one word, one per meaning. */
  senses?: { senseKey?: string; ask: boolean }
): Promise<GeneratedCardContent> {
  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) {
    throw new CardCreationError("LingoPing's server isn't configured.", 'unconfigured');
  }
  const supabase = getSupabase(profile.supabase_url, key);
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    throw new CardCreationError(ERROR_MESSAGES.sign_in_required, 'sign_in_required');
  }

  const { data, error } = await supabase.functions.invoke<{
    content?: unknown;
    choose?: unknown;
    lemma?: unknown;
    suggested?: unknown;
  }>('generate-card', {
    body: {
      lemma: word,
      language,
      translation_language: normalizeTranslationLanguage(profile.native_language),
      context: context?.slice(0, 300),
      hint: hint?.slice(0, 200),
      ...(senses ? { senses: true, sense_key: senses.senseKey, ask: senses.ask } : {}),
    },
  });

  if (error) {
    await throwIfAccessLocked(error);
    if (error instanceof FunctionsFetchError) {
      throw new CardCreationError(
        "Couldn't reach LingoPing's server. Check your connection.",
        'offline'
      );
    }
    const code = error instanceof FunctionsHttpError ? await httpErrorCode(error) : undefined;
    if (code === 'not_a_german_word') {
      throw new CardCreationError(
        `"${word}" ${ERROR_MESSAGES.not_a_german_word}`,
        'not_a_german_word'
      );
    }
    throw new CardCreationError(
      (code && ERROR_MESSAGES[code]) || "Couldn't create the card right now. Please try again.",
      knownCode(code)
    );
  }

  if (data?.choose) {
    const choices = validateWordSenses(data.choose);
    const lemma = typeof data.lemma === 'string' && data.lemma.trim() ? data.lemma.trim() : word;
    if (choices.length > 1) {
      const have = (await cardsFor(lemma, language)).map((c) => c.sense_key ?? '');
      const suggested =
        typeof data.suggested === 'string' && choices.some((s) => s.sense_key === data.suggested)
          ? data.suggested
          : undefined;
      throw new SenseChoiceNeeded(lemma, choices, have, suggested);
    }
  }
  const content = validateGeneratedContent(data?.content);
  if (!content) {
    throw new CardCreationError("Couldn't create the card right now. Please try again.", 'other');
  }
  return content;
}

/** Batch version of the same call: up to 20 words, each still cached and capped individually. */
async function fetchGeneratedContentBatch(
  items: Array<{ word: string; context?: string; hint?: string; senseKey?: string }>,
  language: string,
  /** This device keeps one card per meaning: the server may answer later meanings. */
  senses = false
): Promise<
  Array<{ word: string; content?: GeneratedCardContent; error?: string; code?: CardErrorCode }>
> {
  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) {
    return items.map((i) => ({
      word: i.word,
      error: "LingoPing's server isn't configured.",
      code: 'unconfigured' as const,
    }));
  }
  const supabase = getSupabase(profile.supabase_url, key);
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    return items.map((i) => ({
      word: i.word,
      error: ERROR_MESSAGES.sign_in_required,
      code: 'sign_in_required' as const,
    }));
  }

  const { data, error } = await supabase.functions.invoke<{
    results?: Array<{ word: string; content?: unknown; error?: string }>;
  }>('generate-card', {
    body: {
      language,
      translation_language: normalizeTranslationLanguage(profile.native_language),
      words: items.map((i) => ({
        lemma: i.word,
        context: i.context?.slice(0, 300),
        hint: i.hint?.slice(0, 200),
        ...(senses && i.senseKey !== undefined ? { sense_key: i.senseKey } : {}),
      })),
      ...(senses ? { senses: true } : {}),
    },
  });

  if (error) {
    await throwIfAccessLocked(error);
    // The whole request failed (offline, signed out, server error): same reason for every word.
    const code = error instanceof FunctionsHttpError ? await httpErrorCode(error) : undefined;
    const message =
      error instanceof FunctionsFetchError
        ? "Couldn't reach LingoPing's server. Check your connection."
        : (code && code !== 'not_a_german_word' && ERROR_MESSAGES[code]) ||
          "Couldn't create the card right now. Please try again.";
    // A request-level failure never says anything about the individual words.
    const failure: CardErrorCode =
      error instanceof FunctionsFetchError
        ? 'offline'
        : code === 'not_a_german_word'
          ? 'other'
          : knownCode(code);
    return items.map((i) => ({ word: i.word, error: message, code: failure }));
  }

  const results = data?.results ?? [];
  return items.map((item, i) => {
    const r = results[i];
    const code = r?.error;
    if (!r || code) {
      const message =
        code === 'not_a_german_word'
          ? `"${item.word}" ${ERROR_MESSAGES.not_a_german_word}`
          : (code && ERROR_MESSAGES[code]) ||
            "Couldn't create the card right now. Please try again.";
      return { word: item.word, error: message, code: knownCode(code) };
    }
    const content = validateGeneratedContent(r.content);
    if (!content) {
      return {
        word: item.word,
        error: "Couldn't create the card right now. Please try again.",
        code: 'other' as const,
      };
    }
    return { word: item.word, content };
  });
}

/**
 * Local development with simulated sign-in has no real session, so the server can't be called.
 * Clearly marked as placeholder text.
 */
function devPlaceholderContent(word: string): GeneratedCardContent {
  return {
    lemma: word,
    language: 'de',
    part_of_speech: 'other',
    translation: `[dev placeholder for "${word}"]`,
    ipa: '/-/',
    gender: null,
    article: null,
    plural: null,
    plural_ipa: null,
    example_de: `[dev] Sign in for real to generate an example for "${word}".`,
    example_en: '[dev] Placeholder, generated without the AI server.',
    image_subject: '',
    verb: null,
    image_url: null,
    translations: {},
    sense_key: '',
    sense_hint: null,
    other_senses: [],
  };
}

/**
 * The dev mode without a server: a word of the sample deck (src/dev) gets complete content, any
 * other word the placeholder. The dynamic import is development-only, so the sample deck is not
 * part of staging or production builds.
 */
async function devContent(word: string): Promise<GeneratedCardContent> {
  const sample = import.meta.env.DEV
    ? (await import('../dev/devDeckFixture.js')).devContentFor(word)
    : null;
  return sample ?? devPlaceholderContent(word);
}

async function isDevMockSession(): Promise<boolean> {
  if (!import.meta.env.DEV) return false;
  const userId = await StorageManager.getCurrentUserId();
  return !!userId?.startsWith('dev-');
}

/**
 * Creates a new card for `userId`. The text always comes from the shared word library on the
 * server (generated once by AI, or curated: Basic German); FSRS scheduling state starts fresh for this user. `hint` is an
 * optional translation (e.g. from an imported file) passed to the AI as a hint only - it still
 * writes the card. Throws AccessLockedError when the learner has no access (services/access.ts),
 * and CardCreationError with a user-facing message when the card can't be
 * created.
 */
export async function createVocabularyCard(
  lemma: string,
  userId: string,
  targetLang: string = 'de',
  _nativeLang: string = 'en',
  contextSentence?: string,
  hint?: string,
  source: UpNextSource = { kind: 'user' },
  /**
   * `senseKey`: the meaning the learner chose for a word with several. `ask: false`: nobody is
   * there to choose (a right-click on a web page), so the most likely meaning is taken.
   */
  options: { senseKey?: string; ask?: boolean } = {}
): Promise<Card> {
  const word = lemma.trim();
  // Whether a second meaning of a word may become its own card here (see platform.ts).
  const senses = (await platform.sensesEnabled())
    ? { senseKey: options.senseKey, ask: options.ask !== false }
    : undefined;
  const existing = await existingCardFor(
    withoutArticle(word),
    targetLang,
    undefined,
    senses ? options.senseKey : undefined
  );
  // With meanings, a word that is a card already may still have another meaning to add: the
  // server is asked, and only a meaning the learner has counts as "already in your deck".
  if (existing && (!senses || options.senseKey !== undefined)) {
    throw new DuplicateCardError(existing);
  }
  if (await isDevMockSession()) {
    const dev = import.meta.env.DEV ? await import('../dev/devSenses.js') : null;
    const choice = senses && dev ? dev.devSenseChoice(word, options.senseKey) : null;
    if (choice?.kind === 'ask') {
      const have = (await cardsFor(choice.lemma, targetLang)).map((c) => c.sense_key ?? '');
      if (choice.senses.every((s) => have.includes(s.sense_key))) {
        throw new DuplicateCardError(existing!);
      }
      throw new SenseChoiceNeeded(choice.lemma, choice.senses, have);
    }
    const content = choice?.kind === 'one' ? choice.content : await devContent(word);
    return withUpNext(await cardForContent(content, userId), source);
  }
  try {
    return withUpNext(
      await cardForContent(
        await fetchGeneratedContent(word, targetLang, contextSentence, hint, senses),
        userId
      ),
      source
    );
  } catch (err) {
    // Every meaning of the word is a card already: that is "already in your deck".
    if (err instanceof SenseChoiceNeeded && existing) {
      if (err.senses.every((s) => err.have.includes(s.sense_key))) {
        throw new DuplicateCardError(existing);
      }
    }
    // Offline, a server error or the daily limit say nothing about the word: the learner has
    // chosen it, so it gets a "pending" card now and its text is filled in later (pendingCards.ts).
    if (err instanceof CardCreationError && isRetryableCode(err.code)) {
      const card = buildPendingCard({
        lemma: withoutArticle(word),
        userId,
        language: targetLang,
        source,
        senseKey: senses ? options.senseKey : undefined,
      });
      await rememberPendingCard(card.id, {
        context: contextSentence,
        hint,
        code: err.code,
        attempts: 1,
        lastAttemptAt: new Date().toISOString(),
      });
      return card;
    }
    throw err;
  }
}

/**
 * Creates a typed word's card, asking the learner first when the word has several meanings
 * (`choose` shows the chooser and answers the chosen meanings, or null for "cancel"). One card per
 * chosen meaning; an empty list when the learner cancelled.
 */
export async function createChosenCards(
  word: string,
  userId: string,
  targetLang: string,
  choose: (needed: SenseChoiceNeeded) => Promise<string[] | null>,
  source: UpNextSource = { kind: 'user' },
  /** A sentence the word was seen in: helps the server pick the right meaning. */
  context?: string
): Promise<Card[]> {
  try {
    return [await createVocabularyCard(word, userId, targetLang, 'en', context, undefined, source)];
  } catch (err) {
    if (!(err instanceof SenseChoiceNeeded)) throw err;
    const keys = await choose(err);
    if (!keys?.length) return [];
    const cards: Card[] = [];
    for (const senseKey of keys) {
      cards.push(
        await createVocabularyCard(
          err.lemma,
          userId,
          targetLang,
          'en',
          context,
          undefined,
          source,
          {
            senseKey,
          }
        )
      );
    }
    return cards;
  }
}

export interface BatchCardResult {
  word: string;
  card?: Card;
  error?: string;
  /** Why it failed (set together with `error`). */
  code?: CardErrorCode;
  /** The word (or its dictionary form) is already a card; nothing was created and nothing to retry. */
  duplicate?: boolean;
}

function duplicateResult(word: string, existing: Card): BatchCardResult {
  return { word, error: `"${existing.front_text}" is already in your deck.`, duplicate: true };
}

/** Up to this many words per `generate-card` batch call (the server enforces the same limit). */
const MAX_BATCH = 20;

/**
 * Creates several cards in as few server round trips as possible - a 200-word import makes at most
 * 10 calls instead of 200. Each entry in
 * the result lines up with the matching request (by position); `error` is the same kind of
 * user-facing message `CardCreationError` carries.
 */
export async function createVocabularyCards(
  requests: Array<{
    word: string;
    context?: string;
    hint?: string;
    cardId?: string;
    /** One meaning of a word with several (a deck entry of that meaning). */
    senseKey?: string;
  }>,
  userId: string,
  targetLang: string = 'de'
): Promise<BatchCardResult[]> {
  const results: BatchCardResult[] = new Array(requests.length);
  const senses = await platform.sensesEnabled();
  const pending: Array<{
    index: number;
    word: string;
    context?: string;
    hint?: string;
    cardId?: string;
    senseKey?: string;
  }> = [];
  const lemmasInBatch = new Set<string>();

  for (const [index, req] of requests.entries()) {
    const word = req.word.trim();
    const senseKey = senses ? req.senseKey : undefined;
    // `cardId`: the request is for a pending card that already exists (it is not its own duplicate).
    const existing = await existingCardFor(word, targetLang, req.cardId, senseKey);
    if (existing) {
      results[index] = duplicateResult(word, existing);
    } else {
      pending.push({
        index,
        word,
        context: req.context,
        hint: req.hint,
        cardId: req.cardId,
        senseKey,
      });
    }
  }
  if (pending.length === 0) return results;

  if (await isDevMockSession()) {
    const dev = import.meta.env.DEV ? await import('../dev/devSenses.js') : null;
    for (const p of pending) {
      // The sample words with several meanings are made as the meaning asked for.
      const choice = dev?.devSenseChoice(p.word, p.senseKey ?? '');
      const content = choice?.kind === 'one' ? choice.content : await devContent(p.word);
      results[p.index] = { word: p.word, card: cardFromGeneratedContent(content, userId) };
    }
    return results;
  }

  for (let start = 0; start < pending.length; start += MAX_BATCH) {
    const chunk = pending.slice(start, start + MAX_BATCH);
    const outcomes = await fetchGeneratedContentBatch(
      chunk.map((p) => ({ word: p.word, context: p.context, hint: p.hint, senseKey: p.senseKey })),
      targetLang,
      senses
    );
    for (const [i, outcome] of outcomes.entries()) {
      const { index, word, cardId } = chunk[i];
      if (!outcome.content) {
        results[index] = { word, error: outcome.error, code: outcome.code ?? 'other' };
        continue;
      }
      // Two inputs in one batch can share a dictionary form ("Schiff", "Schiffe") and meaning.
      const key = `${outcome.content.lemma.toLowerCase()}\u0000${outcome.content.sense_key}`;
      if (lemmasInBatch.has(key)) {
        results[index] = {
          word,
          error: `"${outcome.content.lemma}" is already in this import.`,
          duplicate: true,
        };
        continue;
      }
      try {
        results[index] = { word, card: await cardForContent(outcome.content, userId, cardId) };
        lemmasInBatch.add(key);
      } catch (err) {
        results[index] =
          err instanceof DuplicateCardError
            ? duplicateResult(word, err.card)
            : { word, error: (err as Error).message, code: 'other' as const };
      }
    }
  }
  return results;
}

/** Cards made before AI generation (placeholder examples etc.) that should get fresh text. */
export function needsContentUpgrade(card: Card): boolean {
  // A chosen word still waiting for its text is filled in by pendingCards.ts, not upgraded.
  if (card.content_status === 'pending' || card.retired_at) return false;
  const source = card.linguistic_meta?.content_source;
  if (source === 'ai' || source === 'deck') return false;
  return true;
}

let upgrading = false;

/**
 * Replaces the text of older cards with AI-generated text, keeping their review history.
 * Runs in the background, a few cards at a time; `onUpdated` is called for each refreshed card.
 */
export async function upgradeLegacyCards(
  onUpdated?: (card: Card) => void,
  maxCards = 10
): Promise<number> {
  if (upgrading || (await isDevMockSession())) return 0;
  upgrading = true;
  let done = 0;
  try {
    const candidates = (await StorageManager.getCards()).filter(needsContentUpgrade);
    for (const card of candidates.slice(0, maxCards)) {
      let content: GeneratedCardContent;
      try {
        content = await fetchGeneratedContent(card.lemma ?? card.front_text, card.target_language);
      } catch (err) {
        console.warn('[LingoPing] Could not refresh card text:', (err as Error).message);
        if (err instanceof AccessLockedError) break;
        if (err instanceof CardCreationError && /limit|sign in|reach/i.test(err.message)) break;
        continue;
      }
      // Re-read so a review that happened meanwhile is kept.
      const current = (await StorageManager.getCards()).find((c) => c.id === card.id);
      if (!current) continue;
      // The text and picture only: the reviews stay as they are saved.
      const updated = await StorageManager.updateCard(refreshCardContent(current, content));
      if (!updated) continue;
      onUpdated?.(updated);
      done++;
    }
  } finally {
    upgrading = false;
  }
  return done;
}
